import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, GraduationCap, Building2, Calendar, ChevronRight, Star, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import ReviewsSection from "@/components/ReviewsSection";
import SchoolClaimButton from "@/components/SchoolClaimButton";
import SEO from "@/components/SEO";
import SchoolArticlesSection from "@/components/SchoolArticlesSection";
import SchoolBlogPosts from "@/components/SchoolBlogPosts";
import SchoolMap from "@/components/SchoolMap";
import NearbySchools from "@/components/NearbySchools";
import VirtualTourModal from "@/components/VirtualTourModal";
import { useAuth } from "@/lib/auth";
import { api, V1 } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveMedia = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function SchoolDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tour, setTour] = useState(null); // {available, images, school_name}
  const [tourOpen, setTourOpen] = useState(false);
  const [placements, setPlacements] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`${V1}/schools/by-slug/${slug}`)
      .then(async (r) => {
        setSchool(r.data);
        if (r.data?.id) {
          try {
            const c = await api.get(`${V1}/schools/${r.data.id}/campuses`);
            setCampuses(c.data || []);
          } catch { setCampuses([]); }
          try {
            const t = await api.get(`${V1}/schools/${r.data.id}/virtual-tour`);
            setTour(t.data);
          } catch { setTour(null); }
          try {
            const p = await api.get(`${V1}/schools/${r.data.id}/placements`);
            setPlacements(p.data || []);
          } catch { setPlacements([]); }
        }
      })
      .catch(() => setSchool(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-16 text-center text-muted-foreground">Yükleniyor…</div>;
  if (!school) return <div className="p-16 text-center">Okul bulunamadı.</div>;

  return (
    <div data-testid="school-detail-page">
      <SEO
        title={school.name}
        description={school.short_intro || `${school.name} - ${school.province}/${school.district} özel okulu bursluluk sınavları ve tanıtımı.`}
        canonical={`/okul/${school.slug}`}
        image={school.cover_url}
        keywords={`${school.name}, ${school.province} özel okul, bursluluk sınavı, ${school.district}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: school.name,
          address: { "@type": "PostalAddress", addressLocality: school.district, addressRegion: school.province, addressCountry: "TR" },
          telephone: school.phone,
          email: school.email,
          url: `https://bursluluksinavi.com/okul/${school.slug}`,
          image: school.cover_url,
          ...(school.avg_rating > 0 ? {
            aggregateRating: { "@type": "AggregateRating", ratingValue: school.avg_rating, reviewCount: school.review_count },
          } : {}),
        }}
      />
      {/* Cover */}
      <div className="relative aspect-[16/6] max-h-[420px] w-full overflow-hidden bg-muted">
        <img src={resolveMedia(school.cover_url)} alt={school.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 -mt-16 relative">
        <div className="rounded-xl border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
          <div className="flex-1">
            <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
              <Link to="/okullar" className="hover:text-foreground">Okullar</Link>
              <ChevronRight className="h-3 w-3" />
              <span>{school.province}</span>
              <ChevronRight className="h-3 w-3" />
              <span>{school.district}</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{school.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {school.address}</span>
              {school.phone && <span className="inline-flex items-center gap-1"><Phone className="h-4 w-4" /> {school.phone}</span>}
              {school.email && <span className="inline-flex items-center gap-1"><Mail className="h-4 w-4" /> {school.email}</span>}
              {school.website && <a href={school.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-foreground"><Globe className="h-4 w-4" /> Web Sitesi</a>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              {(school.education_levels || []).map((l) => (
                <Badge key={l} variant="secondary" className="uppercase tracking-widest text-[10px]">{l}</Badge>
              ))}
              {school.boarding && <Badge className="bg-accent/10 text-accent border-accent/20">Yatılı</Badge>}
              {school.foreign_language && <Badge variant="outline">Yabancı Dil Ağırlıklı</Badge>}
              {school.avg_rating > 0 && (
                <span className="inline-flex items-center gap-1 text-sm">
                  <Star className="h-4 w-4 fill-accent text-accent" />
                  <b>{school.avg_rating.toFixed(1)}</b>
                  <span className="text-muted-foreground">({school.review_count})</span>
                </span>
              )}
            </div>
          </div>
          {school.upcoming_exams?.length > 0 && (!user || user.role === "parent") && (
            <Button asChild size="lg" className="btn-lift" data-testid="school-apply-cta">
              <Link to={`/basvuru/${school.upcoming_exams[0].id}`}>Bursluluk Başvurusu Yap <ChevronRight className="h-4 w-4" /></Link>
            </Button>
          )}
          <SchoolClaimButton school={school} />
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-12 pb-16">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <h2 className="font-display text-2xl font-bold mb-4">Okul Hakkında</h2>
              <div className="prose prose-slate max-w-none whitespace-pre-line text-foreground/90 leading-relaxed">
                {school.ai_generated_content || school.description || school.short_intro}
              </div>
            </section>
            {placements.length > 0 && (
              <section data-testid="school-placements-section">
                <h2 className="font-display text-2xl font-bold mb-1">Doğrulanmış Yerleştirme Verileri</h2>
                <p className="text-xs text-muted-foreground mb-4">Admin tarafından onaylanmış, okulun kendi bildirdiği geçmiş yıl istatistikleridir.</p>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {placements.map((p) => (
                    <div key={p.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`public-placement-${p.id}`}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.year} · {p.exam_type}</div>
                      <div className="mt-1 font-display text-2xl font-bold">{p.placed_count}<span className="text-sm text-muted-foreground"> / {p.total_applicants} yerleşti</span></div>
                      {p.scholarship_avg_pct != null && <div className="text-sm text-accent font-medium mt-1">Ort. %{p.scholarship_avg_pct} burs</div>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            {school.gallery?.length > 0 && (
              <section>
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                  <h2 className="font-display text-2xl font-bold">Galeri</h2>
                  {tour?.available && (
                    <Button size="sm" variant="outline" onClick={() => setTourOpen(true)} data-testid="virtual-tour-open-btn" className="btn-lift">
                      <Clapperboard className="h-4 w-4" /> Sanal Turu Başlat
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {school.gallery.map((g, i) => (
                    <div key={i} className="aspect-[1024/700] overflow-hidden rounded-lg border border-border">
                      <img src={resolveMedia(g)} alt={`${school.name} galeri ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              </section>
            )}
            {(campuses.length > 0 || (school.lat && school.lng)) ? (
              <section data-testid="school-map-section">
                <h2 className="font-display text-2xl font-bold mb-4">Konum</h2>
                <SchoolMap
                  points={
                    campuses.length > 0
                      ? campuses.filter((c) => c.lat && c.lng).map((c) => ({
                          lat: c.lat, lng: c.lng,
                          title: `${school.name} — ${c.name}`,
                          subtitle: [c.district, c.province].filter(Boolean).join(", "),
                        }))
                      : [{ lat: school.lat, lng: school.lng, title: school.name, subtitle: school.address }]
                  }
                  height={340}
                />
                {campuses.length > 0 && (
                  <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="school-campuses">
                    {campuses.map((c) => (
                      <div key={c.id} className="p-3 rounded-lg border border-border bg-card">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{[c.address, c.district, c.province].filter(Boolean).join(" · ")}</div>
                        {c.phone && <div className="text-xs mt-1">📞 {c.phone}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : (
              <section data-testid="school-map-section">
                <h2 className="font-display text-2xl font-bold mb-4">Konum</h2>
                <div className="rounded-xl border border-dashed border-border bg-muted/30 text-sm text-muted-foreground flex items-center justify-center h-24 px-4 text-center">
                  Konum bilgisi henüz eklenmemiş. Okul yönetimi kampüs koordinatlarını eklediğinde harita burada görünecek.
                </div>
              </section>
            )}
            {school.brochure_url && (
              <section>
                <h2 className="font-display text-2xl font-bold mb-3">Broşür</h2>
                <a href={resolveMedia(school.brochure_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted transition text-sm" data-testid="school-brochure-download">
                  📄 Broşürü İndir (PDF)
                </a>
              </section>
            )}
            {school.faqs?.length > 0 && (
              <section>
                <h2 className="font-display text-2xl font-bold mb-4">Sıkça Sorulan Sorular</h2>
                <Accordion type="single" collapsible className="border border-border rounded-xl">
                  {school.faqs.map((f, i) => (
                    <AccordionItem value={`item-${i}`} key={i} className="px-4">
                      <AccordionTrigger data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
                      <AccordionContent>{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}
            <ReviewsSection school={school} />
            <SchoolArticlesSection school={school} />
            <SchoolBlogPosts school={school} />
            <NearbySchools schoolId={school.id} limit={6} />
          </div>
          <aside className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="overline text-accent mb-2">Bursluluk Sınavları</div>
              <div className="font-display font-bold text-lg mb-4">Yaklaşan Sınavlar</div>
              <div className="space-y-3">
                {(school.upcoming_exams || []).length === 0 && (
                  <div className="text-sm text-muted-foreground">Bu okul için aktif bursluluk sınavı yok.</div>
                )}
                {(school.upcoming_exams || []).map((e) => (
                  <div key={e.id} className="p-3 rounded-lg border border-border bg-background">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(e.exam_date).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                    <div className="font-medium mt-1">{e.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">Sınıflar: {e.grade_levels?.join(", ")}</div>
                    {(!user || user.role === "parent") && (
                      <Button asChild size="sm" className="w-full mt-3" data-testid={`apply-btn-${e.id}`}>
                        <Link to={`/basvuru/${e.id}`}>Başvur</Link>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {school.facilities?.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="overline text-accent mb-2">Kampüs Olanakları</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {school.facilities.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-muted rounded">
                      <Building2 className="h-3 w-3" /> {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(school.tuition_min || school.tuition_max) && (
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="overline text-accent mb-2">Ücret Aralığı</div>
                <div className="font-display text-2xl font-bold">
                  {(school.tuition_min || 0).toLocaleString("tr-TR")} – {(school.tuition_max || 0).toLocaleString("tr-TR")} ₺
                </div>
                <div className="text-xs text-muted-foreground mt-1">Yıllık, KDV dahil. Sadece bilgi amaçlıdır.</div>
              </div>
            )}
          </aside>
        </div>
        {/* Not this school? Add your own */}
        <div className="mt-12 p-6 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5 text-center" data-testid="add-school-cta">
          <div className="overline text-accent mb-2">Kendi Okulunuz mu?</div>
          <div className="font-display text-2xl font-bold mb-2">Aradığınız okul burada yoksa ekleyin</div>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-4">
            Aradığınız okul SanoStudent&apos;ta listelenmiyorsa hemen kendiniz ekleyebilirsiniz.
            Yönetici onayının ardından okulunuz platformda yayına girer.
          </p>
          <Button asChild size="lg" data-testid="add-my-school-btn">
            <Link to="/okul-kayit">➕ Okulumuzu Ekleyelim</Link>
          </Button>
        </div>
      </div>
      <VirtualTourModal
        open={tourOpen}
        onOpenChange={setTourOpen}
        images={tour?.images || []}
        schoolName={school.name}
      />
    </div>
  );
}
